package dev.kotlinfest._2026

import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.jetbrains.compose.web.css.*

const val LatinFontFamily = "Poppins"
const val JapaneseFontFamily = "Noto Sans JP"

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    val reducedMotionMedia = CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("reduce"))

    ctx.stylesheet.registerKeyframes("hero-logo-pop") {
        from {
            Modifier.opacity(0).styleModifier { property("transform", "scale(0)") }
        }
        55.percent {
            Modifier.opacity(1).styleModifier { property("transform", "scale(1.14)") }
        }
        72.percent {
            Modifier.styleModifier { property("transform", "scale(0.94)") }
        }
        86.percent {
            Modifier.styleModifier { property("transform", "scale(1.04)") }
        }
        to {
            Modifier.opacity(1).styleModifier { property("transform", "scale(1)") }
        }
    }

    ctx.stylesheet.registerKeyframes("fade-up") {
        from {
            Modifier.opacity(0).styleModifier { property("transform", "translateY(24px)") }
        }
        to {
            Modifier.opacity(1).styleModifier { property("transform", "translateY(0)") }
        }
    }

    ctx.stylesheet.registerKeyframes("concept-pop") {
        from {
            Modifier.opacity(0).styleModifier {
                property("filter", "blur(12px)")
                property("transform", "scale(0.15) rotate(-4deg)")
            }
        }
        42.percent {
            Modifier.opacity(1).styleModifier {
                property("filter", "blur(0)")
                property("transform", "scale(1.12) rotate(1.5deg)")
            }
        }
        60.percent {
            Modifier.styleModifier {
                property("filter", "blur(0)")
                property("transform", "scale(0.94) rotate(-0.8deg)")
            }
        }
        76.percent {
            Modifier.styleModifier {
                property("filter", "none")
                property("transform", "scale(1.06) rotate(0.4deg)")
            }
        }
        90.percent {
            Modifier.styleModifier { property("transform", "scale(0.99) rotate(-0.1deg)") }
        }
        to {
            Modifier.opacity(1).styleModifier {
                property("filter", "none")
                property("transform", "scale(1) rotate(0)")
            }
        }
    }

    ctx.stylesheet.registerStyleBase(".hero-logo-motion, .hero-fade-up-motion") {
        Modifier.opacity(0)
    }
    ctx.stylesheet.registerStyleBase(".hero-motion-ready .hero-logo-motion") {
        Modifier.styleModifier {
            property("animation", "hero-logo-pop 800ms cubic-bezier(0.22, 1, 0.36, 1) 100ms both")
            property("transform-origin", "center")
        }
    }
    ctx.stylesheet.registerStyleBase(".hero-motion-ready .hero-fade-up-motion") {
        Modifier.styleModifier {
            property(
                "animation",
                "fade-up 520ms cubic-bezier(0.22, 1, 0.36, 1) var(--motion-delay, 0ms) both",
            )
        }
    }
    ctx.stylesheet.registerStyleBase(
        "html.motion-ready .concept-reveal-trigger:not(.is-revealed) .concept-pop-reveal",
    ) {
        Modifier.opacity(0).styleModifier {
            property("filter", "blur(12px)")
            property("transform", "scale(0.15) rotate(-4deg)")
        }
    }
    ctx.stylesheet.registerStyleBase(".concept-reveal-trigger.is-revealed .concept-pop-reveal") {
        Modifier.styleModifier {
            property("animation", "concept-pop 900ms cubic-bezier(0.22, 1, 0.36, 1) both")
            property("transform-origin", "center")
        }
    }
    ctx.stylesheet.registerStyleBase(
        "html.motion-ready .sponsor-grid-reveal:not(.is-revealed) .sponsor-card-reveal",
    ) {
        Modifier.opacity(0).styleModifier { property("transform", "translateY(24px)") }
    }
    ctx.stylesheet.registerStyleBase(".sponsor-grid-reveal.is-revealed .sponsor-card-reveal") {
        Modifier.styleModifier {
            property(
                "animation",
                "fade-up 520ms cubic-bezier(0.22, 1, 0.36, 1) var(--reveal-delay, 0ms) both",
            )
        }
    }
    ctx.stylesheet.registerStyle(
        ".hero-logo-motion, .hero-fade-up-motion, " +
            ".hero-motion-ready .hero-logo-motion, .hero-motion-ready .hero-fade-up-motion, " +
            ".concept-reveal-trigger.is-revealed .concept-pop-reveal, " +
            ".sponsor-grid-reveal.is-revealed .sponsor-card-reveal",
    ) {
        cssRule(reducedMotionMedia) {
            Modifier.opacity(1).styleModifier {
                property("animation", "none")
                property("filter", "none")
                property("transform", "none")
            }
        }
    }
    ctx.stylesheet.registerStyle(".news-entry-accent::after") {
        cssRule(reducedMotionMedia) {
            Modifier.styleModifier { property("transition", "none") }
        }
    }

    // This site does not need scrolling itself, but this is a good demonstration for how you might enable this in your
    // own site. Note that we only enable smooth scrolling unless the user has requested reduced motion, which is
    // considered a best practice.
    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(
                LatinFontFamily,
                JapaneseFontFamily,
                "sans-serif",
            )
            .fontSize(18.px)
            .lineHeight(1.5)
            .color(SitePalette.brand.ink)
            .margin(0.px)
    }

    // Silk dividers only extend 90% by default; we want full width dividers in our site
    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }
}

val EnHeadingStyle = CssStyle.base {
    Modifier
        .fontFamily(LatinFontFamily, "sans-serif")
        .fontWeight(600)
        .lineHeight(1.6)
        .letterSpacing(0.08.em)
}

val EnSectionTitleStyle = CssStyle.base {
    Modifier
        .fontFamily(LatinFontFamily, "sans-serif")
        .fontWeight(600)
        .fontSize(32.px)
        .lineHeight(1.6)
        .letterSpacing(0.08.em)
}

val AccentEnSectionTitleStyle = CssStyle.base {
    Modifier
        .fontFamily(LatinFontFamily, "sans-serif")
        .fontWeight(600)
        .fontSize(32.px)
        .lineHeight(1.6)
        .letterSpacing(0.08.em)
        .color(SitePalette.brand.pink)
}

val JpHeadingStyle = CssStyle.base {
    Modifier
        .fontFamily(JapaneseFontFamily, "sans-serif")
        .fontWeight(700)
        .lineHeight(1.6)
        .letterSpacing(0.08.em)
}

val BodyTextStyle = CssStyle.base {
    Modifier
        .fontFamily(JapaneseFontFamily, "sans-serif")
        .fontWeight(500)
        .fontSize(14.px)
        .lineHeight(1.6)
        .letterSpacing(0.1.em)
}

val JpDetailTextStyle = CssStyle.base {
    Modifier
        .fontFamily(JapaneseFontFamily, "sans-serif")
        .fontWeight(700)
        .fontSize(16.px)
        .lineHeight(1.6)
        .letterSpacing(0.1.em)
}

val EnMetadataStyle = CssStyle.base {
    Modifier
        .fontFamily(LatinFontFamily, "sans-serif")
        .fontWeight(600)
        .fontSize(16.px)
        .lineHeight(1.6)
        .letterSpacing(0.12.em)
}

val ContentContainerStyle = CssStyle.base {
    Modifier.fillMaxWidth().maxContentWidth()
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}
